import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, UserRoundCog } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../app/providers/AuthProvider";
import PageHeader from "../../components/common/PageHeader";
import PlatformActorListCard from "../../components/common/PlatformActorListCard";
import {
  PASSWORD_POLICY_MESSAGE,
  validatePasswordPolicy,
} from "../../utils/passwordPolicy";

const CREATE_RESELLER_ENDPOINT = "/platform-onboarding/resellers";
const PARTNER_LOOKUP_ENDPOINT = "/platform-onboarding/partners/lookup";
const RESELLER_LOOKUP_ENDPOINT = "/platform-onboarding/resellers/lookup";

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.message ||
    fallbackMessage
  );
}

function extractList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.payload)) return data.payload;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export default function CreateResellerPage() {
  const navigate = useNavigate();
  const { role, roleKey } = useAuth();
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    status: "Active",
    ownershipMode: "direct",
    partnerUserId: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);
  const [resellers, setResellers] = useState([]);
  const [isLoadingResellers, setIsLoadingResellers] = useState(false);
  const [listError, setListError] = useState("");

  const normalizedRoleKey = useMemo(() => {
    const rawRole = String(role || "").trim().toLowerCase();
    const rawRoleKey = String(roleKey || "").trim().toLowerCase();

    if (rawRoleKey) return rawRoleKey;
    if (rawRole === "admin" || rawRole === "superadmin") {
      return "platform.super_admin";
    }
    if (rawRole === "partner") return "platform.partner";
    if (rawRole === "reseller") return "platform.reseller";
    return "";
  }, [role, roleKey]);

  const redirectPath =
    normalizedRoleKey === "platform.partner"
      ? "/app/welcomepage"
      : "/app/admin/approvals";

  const canChooseOwnershipMode =
    normalizedRoleKey === "platform.super_admin";
  const canViewResellerList = normalizedRoleKey === "platform.super_admin";
  const requiresPartnerSelection =
    canChooseOwnershipMode && form.ownershipMode === "partner";

  const resetForm = () => {
    setForm({
      fullName: "",
      email: "",
      password: "",
      status: "Active",
      ownershipMode: "direct",
      partnerUserId: "",
    });
    setFieldErrors({});
    setSubmitError("");
  };

  const loadAdminData = useCallback(async () => {
    if (!canChooseOwnershipMode) {
      setPartnerOptions([]);
      setResellers([]);
      setListError("");
      setIsLoadingPartners(false);
      setIsLoadingResellers(false);
      return;
    }

    try {
      setIsLoadingPartners(true);
      setIsLoadingResellers(true);
      setListError("");

      const [partnersResponse, resellersResponse] = await Promise.all([
        axiosClient.get(PARTNER_LOOKUP_ENDPOINT, {
          __skipBizHeader: true,
        }),
        axiosClient.get(RESELLER_LOOKUP_ENDPOINT, {
          __skipBizHeader: true,
        }),
      ]);

      setPartnerOptions(extractList(partnersResponse.data));
      setResellers(extractList(resellersResponse.data));
    } catch (error) {
      const message = getErrorMessage(error, "Failed to load reseller data.");
      setSubmitError(message);
      setListError(message);
      toast.error(message);
    } finally {
      setIsLoadingPartners(false);
      setIsLoadingResellers(false);
    }
  }, [canChooseOwnershipMode]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(current => {
      if (name === "ownershipMode") {
        return {
          ...current,
          ownershipMode: value,
          partnerUserId: value === "partner" ? current.partnerUserId : "",
        };
      }

      return { ...current, [name]: value };
    });
    setFieldErrors(current => ({ ...current, [name]: "" }));
    setSubmitError("");
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.fullName.trim()) {
      nextErrors.fullName = "Full name is required.";
    }

    if (!form.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!isValidEmail(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    const passwordError = validatePasswordPolicy(form.password);
    if (passwordError) {
      nextErrors.password = passwordError;
    }

    if (requiresPartnerSelection && !form.partnerUserId) {
      nextErrors.partnerUserId = "Select the owning partner.";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      setSubmitError("");

      await axiosClient.post(
        CREATE_RESELLER_ENDPOINT,
        {
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          password: form.password,
          status: form.status,
          partnerUserId: requiresPartnerSelection ? form.partnerUserId : null,
        },
        {
          __skipBizHeader: true,
        }
      );

      toast.success("Reseller created successfully");
      if (canViewResellerList) {
        resetForm();
        await loadAdminData();
      } else {
        navigate(redirectPath, { replace: true });
      }
    } catch (error) {
      const message = getErrorMessage(error, "Failed to create reseller.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (name, label, type = "text", autoComplete) => (
    <label className="space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={handleChange}
        autoComplete={autoComplete}
        className={[
          "w-full rounded-[10px] border bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition",
          fieldErrors[name]
            ? "border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
            : "border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100",
        ].join(" ")}
      />
      {fieldErrors[name] ? (
        <div className="text-xs font-medium text-rose-600">{fieldErrors[name]}</div>
      ) : null}
    </label>
  );

  const renderSelect = (
    name,
    label,
    options,
    { placeholder = null, disabled = false } = {}
  ) => (
    <label className="space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
      <select
        name={name}
        value={form[name]}
        onChange={handleChange}
        disabled={disabled}
        className={[
          "w-full rounded-[10px] border bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition",
          fieldErrors[name]
            ? "border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
            : "border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100",
          disabled ? "cursor-not-allowed bg-slate-50" : "",
        ].join(" ")}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {fieldErrors[name] ? (
        <div className="text-xs font-medium text-rose-600">{fieldErrors[name]}</div>
      ) : null}
    </label>
  );

  const partnerSelectOptions = partnerOptions.map(option => ({
    value: option.userId,
    label: `${option.fullName || option.email} (${option.email})`,
  }));

  const ownershipOptions = [
    { value: "direct", label: "Direct Platform" },
    { value: "partner", label: "Partner-linked" },
  ];

  return (
    <div className="min-h-screen bg-[#f3f4f6] py-6">
      <section className="px-4">
        <div className="mx-auto max-w-[1120px] space-y-6">
          <div className="px-4 pt-4 pb-1">
            <PageHeader
              icon={UserRoundCog}
              title="Create Reseller"
              subtitle="Create a reseller and review existing reseller accounts on the same page"
            />
          </div>

          <div className="px-4">
            <div className="rounded-[10px] border border-slate-200 bg-white shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-6 px-5 py-5">
                {submitError ? (
                  <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {submitError}
                  </div>
                ) : null}

                <div className="grid gap-4 md:grid-cols-2">
                  {renderField("fullName", "Full Name", "text", "name")}
                  {renderField("email", "Email", "email", "email")}
                  {renderField("password", "Password", "password", "new-password")}
                  {renderSelect("status", "Account Status", [
                    { value: "Active", label: "Active" },
                    { value: "Hold", label: "Hold" },
                  ])}
                  {canChooseOwnershipMode
                    ? renderSelect("ownershipMode", "Ownership Scope", ownershipOptions)
                    : null}
                  {requiresPartnerSelection
                    ? renderSelect("partnerUserId", "Owning Partner", partnerSelectOptions, {
                        placeholder: isLoadingPartners
                          ? "Loading partners..."
                          : "Select partner",
                        disabled: isLoadingPartners,
                      })
                    : null}
                </div>

                {normalizedRoleKey === "platform.partner" ? (
                  <div className="rounded-[10px] border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    This reseller will be linked to your current partner account automatically.
                  </div>
                ) : null}

                {normalizedRoleKey === "platform.super_admin" ? (
                  <div className="rounded-[10px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    Create the reseller either directly under platform scope or linked to an existing partner.
                  </div>
                ) : null}

                <div className="rounded-[10px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  {PASSWORD_POLICY_MESSAGE}
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting || isLoadingPartners}
                    className="inline-flex items-center gap-2 rounded-[10px] bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Reseller"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {canViewResellerList ? (
            <div className="px-4">
              <PlatformActorListCard
                title="Existing Resellers"
                subtitle="Review reseller accounts, their status, and linked partner ownership."
                actors={resellers}
                loading={isLoadingResellers}
                error={listError}
                emptyMessage="No resellers have been created yet."
                showPartnerColumn
              />
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
